import React from "react";
import { useState } from "react";
import axios from "axios";
import {
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../Context/firebaseConfig";
import { CheckCircle2, XCircle } from "lucide-react";
import {
  FileCard,
  GhostAction,
  PrimaryAction,
  SuccessCard,
} from "../order/OrderPrimitives";
import {
  queueTransactionalEmail,
  transactionalEmails,
} from "../../utils/transactionalEmail";
import { openSafeExternalUrl } from "../../utils/security";

const OrderDelivery = ({ orderDetails, orderNumber }) => {
  const { files, creatorId, editorId } = orderDetails || {};
  const [loading, setLoading] = useState("");

  const getOriginalSignedUrl = async (originalUrl) => {
    // Extract the path after the bucket name (orders_files/)
    // Handle both full URLs and paths with duplicate "original/original"
    let path = originalUrl;
    
    // If it's a full URL, extract the path part
    if (originalUrl.includes('storage.googleapis.com/')) {
      const urlParts = originalUrl.split('storage.googleapis.com/');
      if (urlParts.length > 1) {
        path = urlParts[1].split('?')[0]; // Remove query params
      }
    }
    
    // Remove bucket name if present
    if (path.startsWith('orders_files/')) {
      path = path.substring('orders_files/'.length);
    }
    
    // Fix duplicate "original/original" by removing one
    path = path.replace(/^original\/original\//, 'original/');
    
    // Extract just the filename (last part after /)
    const filename = path.substring(path.lastIndexOf("/") + 1);
    
    const resp = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/files/original/${filename}`
    );
    return resp.data.url;
  };

  const lookupEmailByUid = async (uid) => {
    const snap = await getDoc(doc(db, "Users", uid));
    if (!snap.exists()) throw new Error("User not found: " + uid);
    return snap.data().profileInfo.email;
  };


  const approveOrder = async () => {
    console.log("Approving order: ", {creatorId, editorId, orderNumber});

    setLoading("approve");
    try {
      await axios.post(`${process.env.REACT_APP_ENDPOINT}/order/approve`, {
        creatorId,
        editorId,
        orderNumber,
      });
      const toEmail = await lookupEmailByUid(editorId);
      await queueTransactionalEmail({
        to: toEmail,
        email: transactionalEmails.deliveryApproved({ orderNumber }),
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading("");
    }
  };

  const updateFilesForUserOrders = async (userId, filesPayload) => {
    if (!userId) return;
    try {
      const userRef = doc(db, "Users", userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;

      const userOrders = userSnap.data().orders || {};
      const buckets = ["pending", "current", "previous"];
      const orderKey = orderNumber?.toString();
      let targetBucket = null;

      for (const bucket of buckets) {
        if (userOrders[bucket]?.[orderKey]) {
          targetBucket = bucket;
          break;
        }
      }

      if (!targetBucket) return;

      await updateDoc(userRef, {
        [`orders.${targetBucket}.${orderKey}.files`]: filesPayload,
      });
    } catch (error) {
      console.error(`Error updating files for user ${userId}`, error);
      throw error;
    }
  };

  const rejectOrder = async () => {
    setLoading("reject");
    try {
      // Mark all current files as rejected
      if (files && files.length > 0) {
        const rejectedFiles = files.map(file => ({
          ...file,
          rejected: true
        }));

        // Update files for both creator and editor
        await Promise.all([
          updateFilesForUserOrders(creatorId, rejectedFiles),
          updateFilesForUserOrders(editorId, rejectedFiles),
        ]);
      }

      await axios.post(`${process.env.REACT_APP_ENDPOINT}/order/reject`, {
        creatorId,
        editorId,
        orderNumber,
      });

      const toEmail = await lookupEmailByUid(editorId);
      await queueTransactionalEmail({
        to: toEmail,
        email: transactionalEmails.revisionRequested({ orderNumber }),
      });

      window.location.reload();
    } catch (err) {
      console.error("Error rejecting order:", err);
    } finally {
      setLoading("");
    }
  };


  // Filter files based on status: if pending, only show files with watermarkedUrl
  const isApprovedOrCompleted = orderDetails?.status === "approved" || orderDetails?.status === "completed";
  const isRejectedOrRevision = orderDetails?.status === "rejected" || orderDetails?.status === "revision";
  
  // Show all files when rejected/revision, otherwise filter by watermarkedUrl
  const visibleFiles = isApprovedOrCompleted 
    ? files 
    : isRejectedOrRevision
    ? files
    : files?.filter(file => file?.watermarkedUrl);

  // Check if there are any non-rejected files (new submissions)
  const hasNewFiles = files?.some(file => !file?.rejected && file?.watermarkedUrl);
  
  // Show buttons only if there are new files (not rejected) or if order is not rejected
  const shouldShowButtons = !isRejectedOrRevision || hasNewFiles;

  return (
    <div className="flex w-full flex-col gap-y-5 text-left">
      {isRejectedOrRevision && (
        <p className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm font-medium text-amber-100">
          Order is in revision. Please wait for resubmission.
        </p>
      )}
      
      {visibleFiles?.length > 0 ? (
        <>
          <div className="flex w-full flex-col text-left">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
              Delivered files
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {visibleFiles?.map((file) => {
                const canViewFile = isApprovedOrCompleted 
                  ? (file?.originalUrl || file?.publicUrl)
                  : file?.watermarkedUrl;

                return (
                  <FileCard
                    key={file.id}
                    file={file}
                    onOpen={async () => {
                      if (!canViewFile) return;
                      
                      if (isApprovedOrCompleted) {
                        return openSafeExternalUrl(file?.originalUrl || file?.publicUrl);
                      } else {
                        if (file?.watermarkedUrl) {
                          return openSafeExternalUrl(file.watermarkedUrl);
                        }
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>

          {orderDetails?.status === "approved" ||
            orderDetails?.status === "completed" ? (
            <SuccessCard message="Order has been approved." />
          ) : shouldShowButtons ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-start">
              <PrimaryAction
                onClick={approveOrder}
                disabled={
                  loading === "approve" || orderDetails?.status === "approved"
                }
              >
                <CheckCircle2 size={14} />
                {loading === "approve" ? "Approving..." : "Approve Order"}
              </PrimaryAction>
              <GhostAction
                onClick={rejectOrder}
                disabled={
                  loading === "reject" || orderDetails?.status === "approved"
                }
                className="border-red-400/20 bg-red-500/10 text-red-100 hover:bg-red-500/15"
              >
                <XCircle size={14} />
                {loading === "reject" ? "Rejecting..." : "Reject Order"}
              </GhostAction>
            </div>
          ) : null}
        </>
      ) : (
        <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white/60">
          No files uploaded yet.
        </p>
      )}
    </div>
  );
};
export default OrderDelivery;
